import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ActionButton } from '@communityshare/ui-shared';
import useNextVideo from '@/features/home-videos/api/useNextVideo';
import getWatchLabels from '@/features/home-videos/utils/getWatchLabels';
import { youtubeEmbedUrl } from '@/features/home-videos/utils/youtube';
import { videoShape } from './videoShape';
import TranscriptDisclosure from './TranscriptDisclosure';
import ResourceCard from './ResourceCard';
import LearningHubLink from './LearningHubLink';

/**
 * Video dialog. Headless UI's Dialog gives role="dialog", aria-modal,
 * aria-labelledby (Dialog.Title), the focus trap, Escape to close, scroll
 * locking and focus return to the element focused before it opened.
 *
 * Mount it only while open: unmounting removes the iframe, which stops
 * playback.
 *
 * Next: asked for the current video without blocking the dialog (the
 * server picks the playlist from the user's audience); the Next block shows
 * only when a video comes back. "Watch next video" swaps the content
 * in place, remounts the player, collapses the transcript, moves focus to
 * the title and announces the new video politely.
 *
 * Mobile and tablet: full-viewport panel. lg+: centred 1006px panel over a
 * 20% black backdrop.
 */
VideoDialog.propTypes = {
  initialVideo: videoShape.isRequired,
  onClose: PropTypes.func.isRequired,
  learningHubUrl: PropTypes.string
};

export default function VideoDialog(props) {
  const { initialVideo, onClose, learningHubUrl } = props;
  const { t } = useTranslation();
  const [video, setVideo] = useState(initialVideo);
  const [announcement, setAnnouncement] = useState('');
  const titleRef = useRef(null);
  const advancedRef = useRef(false);
  const { nextVideo } = useNextVideo(video.youtubeVideoId);
  const next = nextVideo ? getWatchLabels(nextVideo, t) : null;

  useEffect(() => {
    if (!advancedRef.current) return;
    titleRef.current?.focus();
  }, [video.youtubeVideoId]);

  const handleNext = () => {
    if (!nextVideo) return;
    advancedRef.current = true;
    setVideo(nextVideo);
    setAnnouncement(t('home_videos.dialog.now_playing', { title: nextVideo.title }));
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="hidden lg:block fixed inset-0 bg-black/20" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex flex-col lg:flex-row lg:items-start lg:justify-center lg:py-[60px] lg:px-4">
          <Dialog.Panel className="relative w-full flex-1 bg-cs-gray-50 p-4 lg:flex-none lg:w-[1006px] lg:max-w-full lg:rounded-[20px] lg:p-[26px] lg:shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <Dialog.Title
                as="h2"
                ref={titleRef}
                tabIndex={-1}
                className="font-semibold text-[30px] leading-[1.2] text-cs-gray-900 focus:outline-none"
              >
                {video.title}
              </Dialog.Title>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('home_videos.dialog.close')}
                className="p-1 -m-1 flex-shrink-0 text-cs-gray-900 hover:text-cs-gray-700 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
              >
                <XMarkIcon className="size-6" strokeWidth={2} aria-hidden="true" />
              </button>
            </div>

            {video.description && (
              <div className="mt-6 flex flex-col gap-1 text-[17px] leading-normal tracking-[0.17px]">
                <p className="!mb-0 font-semibold text-cs-gray-900">{t('home_videos.dialog.learn')}</p>
                <p className="font-normal text-cs-gray-500">{video.description}</p>
              </div>
            )}

            <div className="mt-5 relative w-full [aspect-ratio:16/9] rounded-[10px] bg-cs-gray-200">
              <iframe
                key={video.youtubeVideoId}
                src={youtubeEmbedUrl(video.youtubeVideoId)}
                title={t('home_videos.dialog.player_title', { title: video.title })}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 size-full rounded-[10px] border-0"
              />
            </div>

            {video.transcript && (
              <div className="mt-4">
                <TranscriptDisclosure
                  key={video.youtubeVideoId}
                  transcript={video.transcript}
                  readTimeMinutes={video.readTimeMinutes}
                />
              </div>
            )}

            {video.resource?.url && (
              <div className="mt-3">
                <ResourceCard resource={video.resource} />
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="self-start lg:self-auto">
                <LearningHubLink url={learningHubUrl} icon="none" onNavigate={onClose} />
              </div>

              {nextVideo && (
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4" data-testid="video-dialog-next">
                  <p className="!mb-0 text-[17px] leading-[1.5] tracking-[-0.17px] lg:text-right">
                    <span className="block font-normal text-cs-gray-500">{t('home_videos.dialog.next')}</span>
                    <span className="block font-semibold text-cs-gray-700">
                      {nextVideo.title}
                      {next.duration && <span className="whitespace-nowrap"> {next.duration}</span>}
                    </span>
                  </p>
                  <ActionButton variant="primary" onClick={handleNext} className="whitespace-nowrap flex-shrink-0">
                    <span>{t('home_videos.dialog.watch_next')}</span>
                    <ArrowRightIcon className="size-4" strokeWidth={2} aria-hidden="true" />
                  </ActionButton>
                </div>
              )}
            </div>

            <p className="sr-only" aria-live="polite" data-testid="video-dialog-announcer">{announcement}</p>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
